// Product brief for Short-Term Care Insurance (STCI). See cmTwo.ts for the
// format intent (mechanics a reviewer can't see from numbers alone + things to
// eyeball).

export const shortTermCareBrief = `**Short-Term Care Insurance (STCI)** — traditional/standalone LTC PLUS a hard
benefit-period (day) cap. Mechanically this is the traditional-LTC strategy with
exactly one extra constraint: a limited benefit period. Single-life only (no
joint variant).

Benefit-period (day) cap — **the defining trait**:
- Total *paid* days are capped at \`policyBenefitPeriodMonths × 30\` (e.g. a
  12-month benefit period → 360 payable days). Once consumed, the policy pays
  $0 even if the dollar pool still has money in it.
- The cap binds on ELIGIBLE (post-2-ADL, post-WP) "paying" days, BEFORE the
  dollar draw. Waiting-period days and <2-ADL days do NOT consume the benefit
  period — only days the policy actually pays for.
- Remaining days are carried in \`strategyState.benefitPeriodDaysRemaining\` (in
  DAYS); \`strategyDeltas.benefitPeriodDaysConsumed\` reports the days burned each
  row. Mirrors legacy \`policyBenefitPeriodMonthsRemaining × 30\` and its
  per-phase decrement.
- KNOWN QUIRK (preserved for parity): a row where the **dollar pool is already
  exhausted** ($0 paid) STILL consumes benefit-period days, because legacy
  decrements by the day-clamp result independently of the dollar draw.
- SHARP EDGE: if \`policyBenefitPeriodMonths\` is null/0, the period seeds to 0
  days and the policy **silently pays $0 for the entire claim** — there is no
  "unlimited period" fallback (matches legacy \`?? 0\`). A misconfigured STCI
  policy with no benefit period pays nothing.

Pool model — **single pool, no death benefit** (identical to traditional LTC):
- \`policyMaximumBenefitAmount\` IS the lifetime LTC dollar pool; it seeds the
  \`base_pool\` (less prior utilization). STCI always has a finite pool — never
  unlimited.
- \`eob\` (second pool) is $0 on every row. \`db\` (death benefit) is $0 on every
  row; \`finalizeDeathBenefit → 0\`.
- Whichever cap binds first stops payment: the dollar pool limits dollars, the
  benefit period limits days. STCI's distinguishing case is the benefit period
  exhausting *before* the pool (e.g. 360 days at $150/day = $54k consumed
  against a $100k pool). When the period crosses 0, a \`pool-exhausted\`
  milestone fires with the label "Benefit period exhausted".

Payout model — reimbursement (default) or cash indemnity:
- **Reimbursement** (default): pays min(eligible cost, pro-rated daily cap) from
  the pool, where the daily cap is pro-rated by *payable* (period-clamped) days.
- **Indemnity** (election): pays min(pro-rated cap, pool remaining) as cash,
  still bounded by the benefit period.

Inflation (identical to traditional LTC; STCI is usually elected at 0%):
- Daily cap = daily benefit × the simple/compound factor at the policy
  anniversary, anchored to at-issue. Pool inflation grows the remaining balance
  (compound) or adds a fixed at-issue step (simple). 0% is the trad-LTC parity
  baseline and the realistic case for most STCI illustrations.

Eligibility gates (out-of-pocket reasons): \`pre-policy\`, \`lt-2-adl\`, \`in-wp\`,
same as traditional LTC. Days stranded by an exhausted benefit period carry NO
ineligibility reason (\`null\`) — same as a dollar-pool-exhausted row — and show
up purely as out-of-pocket.

Ground truth — **no carrier illustration exists for STCI in docs/**. There is no
sample STCI PDF or \`field-mapping.md\` in \`packages/shared/src/calculations/docs/\`,
so there are no carrier dollar amounts to reconcile against. Any pool /
daily-benefit / benefit-period figures in a projection are planner-supplied
assumptions, not transcribed carrier terms. Parity is validated against the
traditional-LTC strategy at 0% inflation (STCI ≡ trad-LTC when the period does
not bind) plus hand-computed benefit-period arithmetic.

Things to eyeball:
- The benefit-period mechanic is THE thing to check: cumulative
  \`benefitPeriodDaysConsumed\` should equal \`policyBenefitPeriodMonths × 30\` at
  exhaustion, never more. WP and <2-ADL months consume **0** period days.
- When the period exhausts before the pool, later months pay $0 with full
  out-of-pocket while \`base_pool\` is still large, and the "Benefit period
  exhausted" milestone fires once.
- \`eob\` and \`db\` are $0 on every row — single pool, no death benefit.
- Partial-month period exhaustion pays a pro-rated fraction of the cap (e.g. 10
  remaining days → 10/30 of the cap) and strands the rest out-of-pocket.
`;
